import { useState, useEffect, useMemo } from 'react';
import Head from 'next/head';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Breadcrumbs,
  Link,
  Chip,
  Paper,
  Skeleton,
  useMediaQuery,
  IconButton,
  Drawer,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  Snackbar,
  Alert,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { DashboardLayout } from '../components/dashboard-layout';
import { getAllHelpContent, getAllFaqContent, getHelpSettings } from '../utils/contentful';
import { useParams, useHistory } from 'react-router-dom';

const richTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: 'text.secondary' }}>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <Typography variant="h5" sx={{ mt: 3, mb: 1.5, fontWeight: 600 }}>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
        {children}
      </Typography>
    ),
    [BLOCKS.UL_LIST]: (node, children) => (
      <Box component="ul" sx={{ pl: 3, mb: 2 }}>{children}</Box>
    ),
    [BLOCKS.OL_LIST]: (node, children) => (
      <Box component="ol" sx={{ pl: 3, mb: 2 }}>{children}</Box>
    ),
    [BLOCKS.LIST_ITEM]: (node, children) => (
      <Box component="li" sx={{ mb: 0.5 }}>{children}</Box>
    ),
    [INLINES.HYPERLINK]: (node, children) => (
      <Link href={node.data.uri} target="_blank" rel="noopener noreferrer">
        {children}
      </Link>
    ),
  },
};

const defaultSectionIcons = {
  'livraisons': 'eva:shopping-bag-fill',
  'produits': 'eva:cube-fill',
  'boutiques': 'eva:home-fill',
  'tarifs': 'eva:grid-fill',
  'notifications': 'eva:message-square-fill',
  'mon-compte': 'eva:person-fill',
};

function HelpSidebar({ sections, selectedArticle, onSelectArticle, searchQuery }) {
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedSubSections, setExpandedSubSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const toggleSubSection = (subSectionId) => {
    setExpandedSubSections(prev => ({ ...prev, [subSectionId]: !prev[subSectionId] }));
  };

  // Auto-expand when article is selected
  useEffect(() => {
    if (selectedArticle) {
      const section = sections.find(s =>
        s.directArticles?.some(a => a.id === selectedArticle.id) ||
        s.subSections?.some(ss => ss.articles?.some(a => a.id === selectedArticle.id))
      );
      if (section) {
        setExpandedSections(prev => ({ ...prev, [section.id]: true }));
        const subSection = section.subSections?.find(ss =>
          ss.articles?.some(a => a.id === selectedArticle.id)
        );
        if (subSection) {
          setExpandedSubSections(prev => ({ ...prev, [subSection.id]: true }));
        }
      }
    }
  }, [selectedArticle, sections]);

  return (
    <List sx={{ width: '100%', py: 0 }} component="nav">
      {sections.map((section) => {
        const iconName = section.icon || defaultSectionIcons[section.slug] || 'eva:folder-fill';
        const hasChildren = (section.subSections?.length > 0) || (section.directArticles?.length > 0);
        const isExpanded = expandedSections[section.id];

        return (
          <Box key={section.id}>
            <ListItemButton
              onClick={() => toggleSection(section.id)}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Icon icon={iconName} width={20} />
              </ListItemIcon>
              <ListItemText
                primary={section.title}
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
              />
              {hasChildren && (
                <Icon
                  icon={isExpanded ? 'eva:chevron-down-fill' : 'eva:chevron-right-fill'}
                  width={18}
                />
              )}
            </ListItemButton>

            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {/* Direct articles */}
                {section.directArticles?.map((article) => (
                  <ListItemButton
                    key={article.id}
                    onClick={() => onSelectArticle(article, section)}
                    selected={selectedArticle?.id === article.id}
                    sx={{
                      pl: 6,
                      borderRadius: 1,
                      mb: 0.25,
                      '&.Mui-selected': {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16) },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <Icon icon="eva:file-text-outline" width={16} />
                    </ListItemIcon>
                    <ListItemText
                      primary={article.title}
                      primaryTypographyProps={{ fontSize: '0.8125rem' }}
                    />
                  </ListItemButton>
                ))}

                {/* Sub-sections */}
                {section.subSections?.map((subSection) => (
                  <Box key={subSection.id}>
                    <ListItemButton
                      onClick={() => toggleSubSection(subSection.id)}
                      sx={{
                        pl: 5,
                        borderRadius: 1,
                        mb: 0.25,
                        '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Icon icon="eva:folder-outline" width={16} />
                      </ListItemIcon>
                      <ListItemText
                        primary={subSection.title}
                        primaryTypographyProps={{ fontWeight: 500, fontSize: '0.8125rem' }}
                      />
                      <Icon
                        icon={expandedSubSections[subSection.id] ? 'eva:chevron-down-fill' : 'eva:chevron-right-fill'}
                        width={16}
                      />
                    </ListItemButton>

                    <Collapse in={expandedSubSections[subSection.id]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {subSection.articles?.map((article) => (
                          <ListItemButton
                            key={article.id}
                            onClick={() => onSelectArticle(article, section, subSection)}
                            selected={selectedArticle?.id === article.id}
                            sx={{
                              pl: 8,
                              borderRadius: 1,
                              mb: 0.25,
                              '&.Mui-selected': {
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                                '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16) },
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <Icon icon="eva:file-text-outline" width={16} />
                            </ListItemIcon>
                            <ListItemText
                              primary={article.title}
                              primaryTypographyProps={{ fontSize: '0.8125rem' }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                ))}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </List>
  );
}

function ArticleView({ article, section, subSection }) {
  if (!article) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          textAlign: 'center',
          py: 8,
        }}
      >
        <Icon icon="eva:book-open-outline" width={64} style={{ opacity: 0.3, marginBottom: 16 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Centre d'aide
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sélectionnez un article dans le menu pour commencer.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }} separator={<Icon icon="eva:chevron-right-fill" width={14} />}>
        <Chip
          label={section?.title || 'Aide'}
          size="small"
          icon={<Icon icon={section?.icon || 'eva:folder-fill'} width={14} />}
          sx={{ fontWeight: 500, fontSize: '0.75rem' }}
        />
        {subSection && (
          <Chip
            label={subSection.title}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
        )}
      </Breadcrumbs>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        {article.title}
      </Typography>

      <Box sx={{ '& > *:last-child': { mb: 0 } }}>
        {article.body ? documentToReactComponents(article.body, richTextOptions) : (
          <Typography color="text.secondary">Cet article n'a pas encore de contenu.</Typography>
        )}
      </Box>
    </Box>
  );
}

function FaqView({
  categories,
  richTextOptions,
  expandedCategory,
  setExpandedCategory,
  expandedItem,
  setExpandedItem,
  onSelectItem,
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const q = searchQuery.toLowerCase().trim();

  const filteredCategories = useMemo(() => {
    if (!q) return categories;
    return categories
      .map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (item) =>
            item.question.toLowerCase().includes(q) ||
            (item.tags || []).some((t) => t.toLowerCase().includes(q))
        ),
      }))
      .filter((cat) => cat.items.length > 0 || cat.title.toLowerCase().includes(q));
  }, [categories, q]);

  const totalItems = filteredCategories.reduce((sum, c) => sum + (c.items?.length || 0), 0);

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        placeholder="Rechercher une question..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon icon="eva:search-fill" width={20} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3, maxWidth: 480 }}
      />

      {filteredCategories.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Icon icon="eva:search-outline" width={48} style={{ opacity: 0.3, marginBottom: 16 }} />
          <Typography color="text.secondary">
            Aucune question ne correspond à votre recherche.
          </Typography>
        </Paper>
      )}

      {filteredCategories.map((category) => (
        <Accordion
          key={category.id}
          expanded={expandedCategory === category.id || !!q}
          onChange={(_, isExpanded) => setExpandedCategory(isExpanded ? category.id : null)}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px !important',
            mb: 2,
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary
            expandIcon={<Icon icon="eva:chevron-down-fill" width={20} />}
            sx={{
              px: 3,
              py: 0.5,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                }}
              >
                <Icon icon={category.icon || 'eva:question-mark-circle-fill'} width={20} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {category.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {category.items?.length || 0} question{(category.items?.length || 0) > 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {category.items?.map((item) => (
              <Accordion
                key={item.id}
                expanded={expandedItem === item.id}
                onChange={(_, isExpanded) => {
                  setExpandedItem(isExpanded ? item.id : null);
                  onSelectItem?.(isExpanded ? { item, category } : null);
                }}
                disableGutters
                elevation={0}
                sx={{
                  '&:before': { display: 'none' },
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <AccordionSummary
                  expandIcon={<Icon icon="eva:plus-fill" width={18} />}
                  sx={{ px: 3 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pt: 0, pb: 2 }}>
                  {item.answer
                    ? documentToReactComponents(item.answer, richTextOptions)
                    : <Typography color="text.secondary" variant="body2">Pas de réponse disponible.</Typography>
                  }
                </AccordionDetails>
              </Accordion>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      {!q && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          {categories.length} catégorie{categories.length > 1 ? 's' : ''} · {totalItems} question{totalItems > 1 ? 's' : ''}
        </Typography>
      )}
    </Box>
  );
}

function SectionCards({ sections, onSelectSection }) {
  return (
    <Grid container spacing={3}>
      {sections.map((section) => {
        const iconName = section.icon || defaultSectionIcons[section.slug] || 'eva:folder-fill';
        const articleCount =
          (section.directArticles?.length || 0) +
          (section.subSections?.reduce((sum, ss) => sum + (ss.articles?.length || 0), 0) || 0);

        return (
          <Grid item xs={12} sm={6} md={4} key={section.id}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: (theme) => `0 12px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                  borderColor: 'primary.main',
                },
              }}
              onClick={() => onSelectSection(section)}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    mb: 2,
                  }}
                >
                  <Icon icon={iconName} width={24} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {section.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {articleCount} article{articleCount > 1 ? 's' : ''}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

const Help = () => {
  const { articleSlug, faqItemId } = useParams();
  const history = useHistory();
  const [sections, setSections] = useState([]);
  const [faqCategories, setFaqCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedSubSection, setSelectedSubSection] = useState(null);
  const [selectedFaqItem, setSelectedFaqItem] = useState(null);
  const [expandedFaqCategory, setExpandedFaqCategory] = useState(null);
  const [expandedFaqItem, setExpandedFaqItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [copyTemplate, setCopyTemplate] = useState(
    'Pour trouver la réponse à votre question, consultez cet article :\n\n*TITLEVARIABLE*\nLINKVARIABLE'
  );

  const mdUp = useMediaQuery((theme) => theme.breakpoints.up('md'));

  useEffect(() => {
    Promise.all([getAllHelpContent(), getAllFaqContent(), getHelpSettings()])
      .then(([helpData, faqData, settings]) => {
        setSections(helpData);
        setFaqCategories(faqData);
        if (settings?.copyMessageTemplate) {
          setCopyTemplate(settings.copyMessageTemplate);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load content:', err);
        setError('Impossible de charger le contenu. Vérifiez votre configuration Contentful.');
        setLoading(false);
      });
  }, []);

  // Sync URL → state: when navigating directly to /help/articles/:slug
  useEffect(() => {
    if (!articleSlug || sections.length === 0) return;
    for (const section of sections) {
      const direct = (section.directArticles || []).find(a => a.slug === articleSlug);
      if (direct) {
        setSelectedArticle(direct);
        setSelectedSection(section);
        setSelectedSubSection(null);
        return;
      }
      for (const ss of (section.subSections || [])) {
        const art = (ss.articles || []).find(a => a.slug === articleSlug);
        if (art) {
          setSelectedArticle(art);
          setSelectedSection(section);
          setSelectedSubSection(ss);
          return;
        }
      }
    }
  }, [articleSlug, sections]);

  // Sync URL → state: when navigating directly to /help/faq/:faqItemId
  useEffect(() => {
    if (!faqItemId || faqCategories.length === 0) return;
    for (const category of faqCategories) {
      const item = (category.items || []).find(i => i.id === faqItemId);
      if (item) {
        setActiveTab(1);
        setSelectedFaqItem(item);
        setExpandedFaqCategory(category.id);
        setExpandedFaqItem(item.id);
        return;
      }
    }
  }, [faqItemId, faqCategories]);

  // Alt+D: copy WhatsApp-ready article link
  useEffect(() => {
    const handler = (e) => {
      if (e.altKey && e.code === 'KeyD') {
        let title;
        let url;
        if (activeTab === 0 && selectedArticle) {
          title = selectedArticle.title;
          url = `${window.location.origin}/help/articles/${selectedArticle.slug}`;
        } else if (activeTab === 1 && selectedFaqItem) {
          title = selectedFaqItem.question;
          url = `${window.location.origin}/help/faq/${selectedFaqItem.id}`;
        } else {
          return;
        }
        e.preventDefault();
        const message = copyTemplate
          .replaceAll('TITLEVARIABLE', title)
          .replaceAll('LINKVARIABLE', url);
        navigator.clipboard.writeText(message).then(() => setCopySnackbar(true));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedArticle, selectedFaqItem, activeTab, copyTemplate]);

  const handleSelectArticle = (article, section, subSection) => {
    setSelectedArticle(article);
    setSelectedSection(section);
    setSelectedSubSection(subSection || null);
    setMobileSidebarOpen(false);
    history.push('/help/articles/' + article.slug);
  };

  const handleSelectSection = (section) => {
    const firstArticle =
      section.directArticles?.[0] ||
      section.subSections?.[0]?.articles?.[0];
    if (firstArticle) {
      const subSection = section.subSections?.find(ss =>
        ss.articles?.some(a => a.id === firstArticle.id)
      );
      handleSelectArticle(firstArticle, section, subSection);
    }
  };

  const handleSelectFaqItem = (selection) => {
    if (selection?.item) {
      setSelectedFaqItem(selection.item);
      setExpandedFaqCategory(selection.category?.id || null);
      setExpandedFaqItem(selection.item.id);
      history.push('/help/faq/' + selection.item.id);
    } else {
      setSelectedFaqItem(null);
      setExpandedFaqItem(null);
      history.push('/help');
    }
  };

  // Filter sections based on search
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return sections;

    const q = searchQuery.toLowerCase();
    return sections
      .map((section) => {
        const filteredDirectArticles = (section.directArticles || []).filter(
          (a) => a.title.toLowerCase().includes(q)
        );
        const filteredSubSections = (section.subSections || [])
          .map((ss) => ({
            ...ss,
            articles: (ss.articles || []).filter((a) => a.title.toLowerCase().includes(q)),
          }))
          .filter((ss) => ss.articles.length > 0 || ss.title.toLowerCase().includes(q));

        const sectionMatches = section.title.toLowerCase().includes(q);

        if (sectionMatches || filteredDirectArticles.length > 0 || filteredSubSections.length > 0) {
          return {
            ...section,
            directArticles: sectionMatches ? section.directArticles : filteredDirectArticles,
            subSections: sectionMatches ? section.subSections : filteredSubSections,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [sections, searchQuery]);

  const sidebarContent = (
    <Box sx={{ p: 2 }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Rechercher un article..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon icon="eva:search-fill" width={20} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />
      <HelpSidebar
        sections={filteredSections}
        selectedArticle={selectedArticle}
        onSelectArticle={handleSelectArticle}
        searchQuery={searchQuery}
      />
    </Box>
  );

  if (loading) {
    return (
      <>
        <Head>
          <title>Aide | Loberz</title>
        </Head>
        <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
          <Container maxWidth="lg">
            <Skeleton variant="text" width={200} height={40} sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rounded" height={140} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Head>
          <title>Aide | Loberz</title>
        </Head>
        <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
          <Container maxWidth="lg">
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.05),
              }}
            >
              <Icon icon="eva:alert-triangle-outline" width={48} style={{ color: '#f44336', marginBottom: 16 }} />
              <Typography variant="h6" gutterBottom>
                Erreur de chargement
              </Typography>
              <Typography color="text.secondary">{error}</Typography>
            </Paper>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Head>
        <title>Aide | Loberz</title>
      </Head>
      <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {!mdUp && selectedArticle && activeTab === 0 && (
                <IconButton onClick={() => setMobileSidebarOpen(true)} size="small">
                  <Icon icon="eva:menu-fill" width={24} />
                </IconButton>
              )}
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Centre d'aide
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Retrouvez toutes les informations pour utiliser votre espace Loberz.
            </Typography>
          </Box>

          <Tabs
            value={activeTab}
            onChange={(_, v) => {
              setActiveTab(v);
              setSelectedArticle(null);
              setSelectedSection(null);
              setSelectedSubSection(null);
              setSelectedFaqItem(null);
              setExpandedFaqItem(null);
              history.push('/help');
            }}
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              label="Articles d'aide"
              icon={<Icon icon="eva:book-open-fill" width={18} />}
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            />
            <Tab
              label="Questions fréquentes"
              icon={<Icon icon="eva:message-circle-fill" width={18} />}
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            />
          </Tabs>

          {/* FAQ tab */}
          {activeTab === 1 && (
            <FaqView
              categories={faqCategories}
              richTextOptions={richTextOptions}
              expandedCategory={expandedFaqCategory}
              setExpandedCategory={setExpandedFaqCategory}
              expandedItem={expandedFaqItem}
              setExpandedItem={setExpandedFaqItem}
              onSelectItem={handleSelectFaqItem}
            />
          )}

          {/* Help articles tab - Home view */}
          {activeTab === 0 && !selectedArticle && (
            <SectionCards sections={filteredSections} onSelectSection={handleSelectSection} />
          )}

          {/* Article view with sidebar */}
          {activeTab === 0 && selectedArticle && (
            <Grid container spacing={3}>
              {/* Sidebar */}
              {mdUp ? (
                <Grid item md={3.5}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      position: 'sticky',
                      top: 80,
                      maxHeight: 'calc(100vh - 120px)',
                      overflow: 'auto',
                    }}
                  >
                    <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Link
                        component="button"
                        underline="hover"
                        onClick={() => {
                          setSelectedArticle(null);
                          setSelectedSection(null);
                          setSelectedSubSection(null);
                          history.push('/help');
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                        }}
                      >
                        <Icon icon="eva:arrow-back-fill" width={16} />
                        Retour à l'accueil
                      </Link>
                    </Box>
                    {sidebarContent}
                  </Paper>
                </Grid>
              ) : (
                <Drawer
                  anchor="left"
                  open={mobileSidebarOpen}
                  onClose={() => setMobileSidebarOpen(false)}
                  PaperProps={{ sx: { width: 300, pt: 2 } }}
                >
                  <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Link
                      component="button"
                      underline="hover"
                      onClick={() => {
                        setSelectedArticle(null);
                        setMobileSidebarOpen(false);
                        history.push('/help');
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                      }}
                    >
                      <Icon icon="eva:arrow-back-fill" width={16} />
                      Retour à l'accueil
                    </Link>
                  </Box>
                  {sidebarContent}
                </Drawer>
              )}

              {/* Article content */}
              <Grid item xs={12} md={8.5}>
                <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2.5, md: 4 } }}>
                  <ArticleView
                    article={selectedArticle}
                    section={selectedSection}
                    subSection={selectedSubSection}
                  />
                </Paper>
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>
      <Snackbar
        open={copySnackbar}
        autoHideDuration={3000}
        onClose={() => setCopySnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopySnackbar(false)} severity="success" sx={{ width: '100%' }}>
          Message de partage avec ce lien généré et copié !
        </Alert>
      </Snackbar>
    </>
  );
};

Help.getLayout = (page) => <DashboardLayout>{page}</DashboardLayout>;

export default Help;
